export interface GPUReview {
  gpuId: string;
  source: string;
  rating: number;
  pros: string[];
  cons: string[];
  verdict: string;
}

export const gpuReviews: Record<string, GPUReview> = {
  'rtx-5090': {
    gpuId: 'rtx-5090',
    source: 'Tech Reviewers Consensus',
    rating: 9.2,
    pros: [
      'Unmatched 4K and 8K performance',
      'Exceptional ray tracing capabilities',
      'DLSS 4 with Multi Frame Generation',
      '32GB GDDR7 future-proofs for years'
    ],
    cons: [
      'Extremely high power consumption (575W)',
      'Premium pricing at $1999',
      'Requires high-end PSU and cooling',
      'Overkill for most gamers'
    ],
    verdict: 'The RTX 5090 is the ultimate GPU for enthusiasts who demand the absolute best performance regardless of cost. Perfect for 4K+ gaming and professional workloads.'
  },
  'rtx-4090': {
    gpuId: 'rtx-4090',
    source: 'Tech Reviewers Consensus',
    rating: 9.0,
    pros: [
      'Still excellent 4K gaming performance',
      'Proven reliability and driver maturity',
      'Great for AI/ML workloads',
      '24GB VRAM handles any game'
    ],
    cons: [
      'High power consumption (450W)',
      'Large physical size',
      'Premium pricing',
      'Being replaced by RTX 5090'
    ],
    verdict: 'The RTX 4090 remains a powerhouse and now offers better value as the 5090 launches. Excellent choice for high-end gaming and content creation.'
  },
  'rtx-5080': {
    gpuId: 'rtx-5080',
    source: 'Tech Reviewers Consensus',
    rating: 8.8,
    pros: [
      'Excellent 4K gaming performance',
      'DLSS 4 support',
      'More reasonable power consumption',
      'Great price-to-performance'
    ],
    cons: [
      'Only 16GB VRAM',
      'Not a huge leap over 4080 Super',
      'Still expensive at $999'
    ],
    verdict: 'The RTX 5080 is an excellent high-end option for 4K gaming without the extreme cost of the 5090.'
  },
  'rtx-4080-super': {
    gpuId: 'rtx-4080-super',
    source: 'Tech Reviewers Consensus',
    rating: 8.5,
    pros: [
      'Excellent 4K gaming',
      'Good ray tracing performance',
      'Reasonable power consumption',
      'DLSS 3 Frame Generation'
    ],
    cons: [
      'High price point',
      '16GB VRAM may limit longevity',
      'Being superseded by RTX 50 series'
    ],
    verdict: 'A solid choice for 4K gaming with great features, though consider waiting for 50-series deals.'
  },
  'rtx-5070-ti': {
    gpuId: 'rtx-5070-ti',
    source: 'Tech Reviewers Consensus',
    rating: 8.7,
    pros: [
      'RTX 4080 performance at lower price',
      'DLSS 4 with Multi Frame Generation',
      '16GB GDDR7 VRAM',
      'Efficient power consumption'
    ],
    cons: [
      'Limited availability at launch',
      'May still be pricey for some'
    ],
    verdict: 'Incredible value proposition bringing high-end performance to a more accessible price point.'
  },
  'rtx-5070': {
    gpuId: 'rtx-5070',
    source: 'Tech Reviewers Consensus',
    rating: 8.5,
    pros: [
      'RTX 4090 performance at $549',
      'DLSS 4 support',
      'Great for 1440p gaming',
      'Efficient 250W TDP'
    ],
    cons: [
      'Only 12GB VRAM',
      'May struggle with future 4K titles'
    ],
    verdict: 'The best value in the 50-series lineup, offering incredible performance per dollar.'
  },
  'rtx-4070-super': {
    gpuId: 'rtx-4070-super',
    source: 'Tech Reviewers Consensus',
    rating: 8.3,
    pros: [
      'Great 1440p performance',
      'Reasonable price at $599',
      'DLSS 3 support',
      'Efficient power usage'
    ],
    cons: [
      '12GB VRAM limitation',
      'Being replaced by 5070'
    ],
    verdict: 'Solid 1440p gaming card, but the RTX 5070 offers better value at similar price.'
  },
  'rx-7900-xtx': {
    gpuId: 'rx-7900-xtx',
    source: 'Tech Reviewers Consensus',
    rating: 8.4,
    pros: [
      'Excellent rasterization performance',
      '24GB VRAM',
      'More affordable than RTX 4080',
      'Great for compute workloads'
    ],
    cons: [
      'Weaker ray tracing than NVIDIA',
      'FSR not as good as DLSS',
      'Higher power consumption'
    ],
    verdict: 'Best AMD option for high-end gaming with excellent value in pure rasterization.'
  },
  'rx-9070-xt': {
    gpuId: 'rx-9070-xt',
    source: 'Tech Reviewers Consensus',
    rating: 8.6,
    pros: [
      'Significantly improved ray tracing',
      'Competitive with RTX 4080 Super',
      'Great price at $649',
      '16GB VRAM'
    ],
    cons: [
      'Still behind NVIDIA in RT',
      'New architecture may have driver issues'
    ],
    verdict: 'AMD\'s best GPU yet, closing the gap with NVIDIA while maintaining price advantage.'
  },
  'rx-9070': {
    gpuId: 'rx-9070',
    source: 'Tech Reviewers Consensus',
    rating: 8.4,
    pros: [
      'Excellent price-to-performance',
      'Good 1440p gaming',
      'Improved ray tracing',
      '16GB VRAM'
    ],
    cons: [
      'RT still trails NVIDIA',
      'Limited availability'
    ],
    verdict: 'Great mid-range option from AMD with competitive performance and value.'
  },
  'rx-7800-xt': {
    gpuId: 'rx-7800-xt',
    source: 'Tech Reviewers Consensus',
    rating: 8.2,
    pros: [
      'Excellent 1440p performance',
      '16GB VRAM',
      'Good value at $499',
      'Reasonable power consumption'
    ],
    cons: [
      'Ray tracing performance lags',
      'FSR quality varies by game'
    ],
    verdict: 'Best value 1440p GPU with generous VRAM for future-proofing.'
  },
  'arc-b580': {
    gpuId: 'arc-b580',
    source: 'Tech Reviewers Consensus',
    rating: 8.0,
    pros: [
      'Incredible value at $249',
      '12GB VRAM',
      'Great DX12/Vulkan performance',
      'XeSS upscaling'
    ],
    cons: [
      'DX11 performance still weak',
      'Driver maturity concerns',
      'Limited RT performance'
    ],
    verdict: 'The budget king with impressive features and performance for the price.'
  },
};

export function getGPUReview(gpuId: string): GPUReview | undefined {
  return gpuReviews[gpuId];
}
